import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import About from './components/About';
import OurIndustry from './components/OurIndustry';
import ServicesDetailed from './components/ServicesDetailed';
import ServiceDetail from './components/ServiceDetail';
import Contact from './components/Contact';
import Career from './components/Career';
import CareerInfo from './components/CareerInfo';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import CookiePolicy from './components/CookiePolicy';
import CookieConsent from './components/CookieConsent';
import SEOHead from './components/SEOHead';
import companyConfig from './config/companyConfig';
import './App.css';

// ScrollToTop component to handle page navigation scrolling
function ScrollToTop() {
  const location = useLocation();
  
  useEffect(() => {
    // Scroll to top when route changes
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth' // Smooth scrolling animation
    });
  }, [location.pathname]); // Trigger when pathname changes
  
  return null; // This component doesn't render anything
}

function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);
  
  return (
    <nav className={`navbar navbar-creative ${scrolled ? 'navbar-scrolled' : ''}`}>
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <div className="logo-wrapper">
            <img src="/images/hero/main-logo.png" alt={`${companyConfig.company.name} Logo`} className="logo-image" />
            <span className="logo-text">{companyConfig.company.name}</span>
          </div>
          <div className="logo-tagline">{companyConfig.company.tagline}</div>
        </Link>

        {/* Desktop Navigation */}
        <ul className="nav-menu">
          <li className="nav-item">
            <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>
              <span className="nav-icon"><i className="fas fa-home"></i></span>
              <span className="nav-text">Home</span>
              <span className="nav-indicator"></span>
            </Link>
          </li>
          <li className="nav-item">
            <Link to="/about" className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`}>
              <span className="nav-icon"><i className="fas fa-info-circle"></i></span>
              <span className="nav-text">About Us</span>
              <span className="nav-indicator"></span>
            </Link>
          </li>
          <li className="nav-item">
            <Link to="/our-industry" className={`nav-link ${location.pathname === '/our-industry' ? 'active' : ''}`}>
              <span className="nav-icon"><i className="fas fa-cogs"></i></span>
              <span className="nav-text">Solutions & Capabilities</span>
              <span className="nav-indicator"></span>
            </Link>
          </li>
          <li className="nav-item">
            <Link to="/services" className={`nav-link ${location.pathname === '/services' ? 'active' : ''}`}>
              <span className="nav-icon"><i className="fas fa-briefcase"></i></span>
              <span className="nav-text">Services</span>
              <span className="nav-indicator"></span>
            </Link>
          </li>
          <li className="nav-item">
            <Link to="/career" className={`nav-link ${location.pathname === '/career' ? 'active' : ''}`}>
              <span className="nav-icon"><i className="fas fa-user-tie"></i></span>
              <span className="nav-text">Career</span>
              <span className="nav-indicator"></span>
            </Link>
          </li>
          <li className="nav-item nav-item-cta">
            <Link to="/contact" className="nav-link-cta">
              <span className="cta-icon"><i className="fas fa-paper-plane"></i></span>
              <span className="cta-text">Contact Us</span>
            </Link>
          </li>
        </ul>

        {/* Mobile Menu Toggle */}
        <button 
          className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${mobileMenuOpen ? 'active' : ''}`}>
          <div className="mobile-menu-header">
            <span className="mobile-menu-title">Menu</span>
            <button 
              className="mobile-menu-close"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          <ul className="mobile-nav-list">
            <li className="mobile-nav-item">
              <Link to="/" className={`mobile-nav-link ${location.pathname === '/' ? 'active' : ''}`}>
                <i className="fas fa-home"></i>
                <span>Home</span>
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link to="/about" className={`mobile-nav-link ${location.pathname === '/about' ? 'active' : ''}`}>
                <i className="fas fa-info-circle"></i>
                <span>About Us</span>
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link to="/our-industry" className={`mobile-nav-link ${location.pathname === '/our-industry' ? 'active' : ''}`}>
                <i className="fas fa-cogs"></i>
                <span>Solutions & Capabilities</span>
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link to="/services" className={`mobile-nav-link ${location.pathname === '/services' ? 'active' : ''}`}>
                <i className="fas fa-briefcase"></i>
                <span>Services</span>
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link to="/career" className={`mobile-nav-link ${location.pathname === '/career' ? 'active' : ''}`}>
                <i className="fas fa-user-tie"></i>
                <span>Career</span>
              </Link>
            </li>
            <li className="mobile-nav-item mobile-nav-item-cta">
              <Link to="/contact" className="mobile-nav-link-cta">
                <i className="fas fa-paper-plane"></i>
                <span>Contact Us</span>
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
}

function HomePage() {
  // Rotating badge state
  const [badgeIndex, setBadgeIndex] = useState(0);
  const [fadeClass, setFadeClass] = useState('');
  
  // Counter animation states
  const [clients, setClients] = useState(0);
  const [uptime, setUptime] = useState(0);
  const [countries, setCountries] = useState(0);
  
  // SAP Module modal state
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleType, setModuleType] = useState('functional'); // 'functional' or 'technical'


  
  // Rotate through Why Choose Us reasons every 4 seconds with fade effect
  useEffect(() => {
    const interval = setInterval(() => {
      setFadeClass('fade-out');
      setTimeout(() => {
        setBadgeIndex((prev) => (prev + 1) % companyConfig.whyChooseUs.reasons.length);
        setFadeClass('fade-in');
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, []);
  
  // Counter animation effect
  useEffect(() => {
    const duration = 2000; // 2 seconds animation
    const steps = 60; // number of steps
    const stepDuration = duration / steps;
    
    // Parse target values
    const targetClients = parseInt(companyConfig.stats.clients);
    const targetUptime = parseFloat(companyConfig.stats.uptime);
    const targetCountries = parseInt(companyConfig.stats.countries);
    
    let currentStep = 0;
    
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      setClients(Math.floor(targetClients * progress));
      setUptime((targetUptime * progress).toFixed(1));
      setCountries(Math.floor(targetCountries * progress));
      
      if (currentStep >= steps) {
        setClients(targetClients);
        setUptime(targetUptime);
        setCountries(targetCountries);
        clearInterval(timer);
      }
    }, stepDuration);
    
    return () => clearInterval(timer);
  }, []);
  
  return (
    <>
      <SEOHead 
        title="Time2Find - Enterprise IT Solutions & Digital Transformation"
        description="Leading provider of SAP Implementation, Cloud Solutions, Cybersecurity, Data Analytics, IoT Solutions, and Quality Assurance. Transform your business with our 25+ years of expertise in enterprise IT solutions."
        keywords="SAP Implementation, Cloud Solutions, Cybersecurity, Data Analytics, IoT Solutions, Quality Assurance, Enterprise IT, Digital Transformation, Business Solutions, IT Consulting, Time2Find"
        canonical="https://time2find.com/"
      />
      
      {/* Hero Section */}
      <section className="hero-new">
        {/* 3D Divider Line at top of Hero */}
        <div className="navbar-divider-3d"></div>
        
        <div className="hero-container">
          <div className="hero-content">
            {/* Left Side - Content */}
            <div className="hero-left-new">
              <div className="hero-badge">
                <span className="badge-icon">✦</span>
                Trusted by {companyConfig.stats.clients} Global Enterprises
              </div>
              
              <h1 className="hero-title-new">
                Enterprise IT <span className="highlight-black">Transformation</span> Made Simple
              </h1>
              
              <p className="hero-description">
                Accelerate your digital journey with our comprehensive IT consulting services. From cloud migration to cybersecurity, we deliver enterprise-grade solutions that drive innovation and growth.
              </p>
              
              <div className="hero-buttons">
                <Link to="/services">
                  <button className="hero-btn-primary">
                    Start Your Transformation
                    <i className="fas fa-arrow-right"></i>
                  </button>
                </Link>
              </div>
              
              {/* Stats Row */}
              <div className="hero-stats">
                <div className="hero-stat-item">
                  <div className="stat-number-new">{clients}+</div>
                  <div className="stat-label-new">Enterprise Clients</div>
                </div>
                <div className="hero-stat-divider"></div>
                <div className="hero-stat-item">
                  <div className="stat-number-new">{uptime}%</div>
                  <div className="stat-label-new">Service Uptime</div>
                </div>
                <div className="hero-stat-divider"></div>
                <div className="hero-stat-item">
                  <div className="stat-number-new">{countries}+</div>
                  <div className="stat-label-new">Countries Served</div>
                </div>
              </div>
            </div>
            
            {/* Right Side - 3D Laptop Visual */}
            <div className="hero-right-new">
              <div className="laptop-container">
                <div className="gradient-glow"></div>
                <img 
                  src="/images/hero/sap home.png" 
                  alt="SAP Solutions Dashboard" 
                  className="laptop-image"
                />
                <div className="floating-badge">
                  <i className="fas fa-shield-alt"></i>
                  <span className={fadeClass}>{companyConfig.whyChooseUs.reasons[badgeIndex].title}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Client Logos in Hero Section */}
          <div className="hero-clients-section">
            <div className="hero-clients-container">
              <p className="hero-clients-text">Trusted by leading enterprises worldwide</p>
              <div className="hero-client-logos">
                <div className="hero-logo-track">
                  {companyConfig.images.clients.slice(0, 8).map((clientLogo, index) => (
                    <div key={`hero-client-${index}`} className="hero-logo-item">
                      <img 
                        src={clientLogo} 
                        alt={`Client ${index + 1}`}
                        onError={(e) => {
                          e.target.src = `https://via.placeholder.com/100x40/E5E7EB/6B7280?text=Client+${index + 1}`;
                        }}
                      />
                    </div>
                  ))}
                  {companyConfig.images.clients.slice(0, 8).map((clientLogo, index) => (
                    <div key={`hero-client-dup-${index}`} className="hero-logo-item">
                      <img 
                        src={clientLogo} 
                        alt={`Client ${index + 1}`}
                        onError={(e) => {
                          e.target.src = `https://via.placeholder.com/100x40/E5E7EB/6B7280?text=Client+${index + 1}`;
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Optional decorative tiles */}
              <div className="hero-tiles-decoration">
                {[...Array(12)].map((_, index) => (
                  <div key={index} className="hero-tile" style={{ animationDelay: `${index * 0.1}s` }}></div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Hero Section Divider */}
        <div className="hero-bottom-divider"></div>
      </section>
        
      {/* About Section */}
      <section className="about">
          <div className="about-left">
            <h2 className="about-title">About Time2Find</h2>
            <p className="about-paragraph">
              25+ years delivering secure, scalable enterprise IT solutions across the globe. We partner with organizations to transform their digital landscape and drive innovation through cutting-edge technology and strategic consulting.
            </p>
            <div className="info-cards">
              <div className="info-card">
                <div className="info-icon"><i className="fas fa-user-tie"></i></div>
                <div className="info-number">{companyConfig.company.experience}</div>
                <div className="info-label">Year Experience</div>
              </div>
              <div className="info-card">
                <div className="info-icon"><i className="fas fa-globe"></i></div>
                <div className="info-number">{companyConfig.stats.clients}</div>
                <div className="info-label">Global Clients</div>
              </div>
              <div className="info-card">
                <div className="info-icon"><i className="fas fa-lock"></i></div>
                <div className="info-number">{companyConfig.stats.security}</div>
                <div className="info-label">Security Certified</div>
              </div>
              <div className="info-card">
                <div className="info-icon"><i className="fas fa-trophy"></i></div>
                <div className="info-number">{companyConfig.stats.successRate}</div>
                <div className="info-label">Success Rate</div>
              </div>
            </div>
          </div>
          <div className="about-right">
            <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop" alt="Office Meeting" className="about-image" />
          </div>
        </section>

        {/* Who We Are Section - 3D Card Effect */}
        <section className="who-we-are-section-3d">
          <div className="who-we-are-container-3d">
            <div className="who-we-are-header-3d">
              <div className="section-badge-3d">
                <i className="fas fa-building"></i>
              </div>
              <h2 className="who-we-are-title-3d">{companyConfig.whoWeAre.title}</h2>
              <p className="who-we-are-subtitle-3d">{companyConfig.whoWeAre.subtitle}</p>
            </div>
            
            <div className="who-cards-3d-grid">
              {companyConfig.whoWeAre.cards.map((card, index) => (
                <div 
                  key={index} 
                  className={`who-card-3d ${index % 2 === 0 ? 'blue-theme-3d' : 'orange-theme-3d'}`}
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <div className="card-3d-inner">
                    <div className="card-3d-icon">
                      <i className={`fas ${card.icon}`}></i>
                    </div>
                    <h3 className="card-3d-title">{card.title}</h3>
                    <div className="card-3d-content">
                      {card.description.map((paragraph, pIndex) => (
                        <p key={pIndex} className="card-3d-text">{paragraph}</p>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SAP Solutions & Services Section - Professional Redesign */}
        <section className="sap-solutions-section">
          <div className="sap-solutions-container">
            <div className="sap-solutions-header">
              <h2 className="sap-solutions-title">{companyConfig.sapSolutions.title}</h2>
              <p className="sap-solutions-subtitle">{companyConfig.sapSolutions.subtitle}</p>
            </div>

            {/* Traditional Modules Grid */}
            <div className="sap-traditional-section">
              <div className="sap-category-header">
                <div className="category-badge functional-badge">
                  <i className="fas fa-briefcase"></i>
                </div>
                <h3 className="category-title">Functional Modules</h3>
              </div>
              
              <div className="sap-modules-grid">
                {companyConfig.sapSolutions.traditional.modules.slice(0, 5).map((module, index) => (
                  <div key={index} className="sap-module-card" onClick={() => {
                    setSelectedModule(module);
                    setModuleType('functional');
                  }}>
                    <div className="module-card-header">
                      <div className="module-icon-circle">
                        <i className={`fas ${module.icon}`}></i>
                      </div>
                      <h4 className="module-name">{module.name}</h4>
                    </div>
                    <p className="module-fullname">{module.fullName}</p>
                    <div className="module-card-footer">
                      <span className="learn-more">Learn More <i className="fas fa-arrow-right"></i></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="sap-traditional-section">
              <div className="sap-category-header">
                <div className="category-badge technical-badge">
                  <i className="fas fa-code"></i>
                </div>
                <h3 className="category-title">Technical Modules</h3>
              </div>
              
              <div className="sap-modules-grid">
                {companyConfig.sapSolutions.traditional.modules.slice(5, 10).map((module, index) => (
                  <div key={index} className="sap-module-card" onClick={() => {
                    setSelectedModule(module);
                    setModuleType('technical');
                  }}>
                    <div className="module-card-header">
                      <div className="module-icon-circle">
                        <i className={`fas ${module.icon}`}></i>
                      </div>
                      <h4 className="module-name">{module.name}</h4>
                    </div>
                    <p className="module-fullname">{module.fullName}</p>
                    <div className="module-card-footer">
                      <span className="learn-more">Learn More <i className="fas fa-arrow-right"></i></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SAP Module Modal */}
        {selectedModule && (
          <div className="sap-modal-overlay" onClick={() => setSelectedModule(null)}>
            <div className={`sap-modal-content ${moduleType === 'technical' ? 'technical-theme' : 'functional-theme'}`} onClick={(e) => e.stopPropagation()}>
              <button className="sap-modal-close" onClick={() => setSelectedModule(null)}>
                <i className="fas fa-times"></i>
              </button>
              
              <div className="sap-modal-header">
                <div className="sap-modal-icon">
                  <i className={`fas ${selectedModule.icon}`}></i>
                </div>
                <div className="sap-modal-title-section">
                  <h3 className="sap-modal-title">{selectedModule.name}</h3>
                  <p className="sap-modal-subtitle">{selectedModule.fullName}</p>
                </div>
              </div>

              <div className="sap-modal-body">
                <div className="sap-modal-section">
                  <h4 className="sap-modal-section-title">
                    <i className="fas fa-info-circle"></i> Overview
                  </h4>
                  <p className="sap-modal-text">
                    {selectedModule.description || `${selectedModule.fullName} is a comprehensive SAP solution designed to streamline and optimize business processes. Our expert consultants provide end-to-end implementation, customization, and support services to maximize your investment.`}
                  </p>
                </div>

                <div className="sap-modal-section">
                  <h4 className="sap-modal-section-title">
                    <i className="fas fa-check-circle"></i> Key Features
                  </h4>
                  <ul className="sap-modal-features">
                    <li><i className="fas fa-chevron-right"></i> Complete module implementation and configuration</li>
                    <li><i className="fas fa-chevron-right"></i> Integration with existing SAP landscape</li>
                    <li><i className="fas fa-chevron-right"></i> Custom development and enhancements</li>
                    <li><i className="fas fa-chevron-right"></i> User training and change management</li>
                    <li><i className="fas fa-chevron-right"></i> Ongoing support and optimization</li>
                  </ul>
                </div>

                <div className="sap-modal-section">
                  <h4 className="sap-modal-section-title">
                    <i className="fas fa-chart-line"></i> Business Benefits
                  </h4>
                  <div className="sap-modal-benefits">
                    <div className="sap-benefit-card">
                      <i className="fas fa-tachometer-alt"></i>
                      <span>Improved Efficiency</span>
                    </div>
                    <div className="sap-benefit-card">
                      <i className="fas fa-coins"></i>
                      <span>Cost Reduction</span>
                    </div>
                    <div className="sap-benefit-card">
                      <i className="fas fa-eye"></i>
                      <span>Better Visibility</span>
                    </div>
                    <div className="sap-benefit-card">
                      <i className="fas fa-shield-alt"></i>
                      <span>Compliance</span>
                    </div>
                  </div>
                </div>

                <div className="sap-modal-cta">
                  <Link to="/contact" className="sap-modal-button primary" onClick={() => setSelectedModule(null)}>
                    <i className="fas fa-envelope"></i> Get Started
                  </Link>
                  <Link to="/services" className="sap-modal-button secondary" onClick={() => setSelectedModule(null)}>
                    <i className="fas fa-info-circle"></i> View All Services
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Services Section */}
        <section className="services">
          <div className="services-header">
            <h2 className="services-title">Our Core Services</h2>
            <p className="services-subtitle">
              Comprehensive IT solutions tailored to drive your business forward in the digital age.
            </p>
          </div>
          <div className="service-cards">
            {companyConfig.services.map(service => (
              <Link key={service.id} to={`/services/${service.slug}`} className="service-card">
                <div className="service-icon"><i className={service.icon}></i></div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.shortDescription}</p>
                <div className="service-buttons">
                  <button className="service-btn-primary">Consult Now</button>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Customer Reviews / Testimonials Section */}
      <section className="testimonials-section">
        <div className="testimonials-container">
          <div className="testimonials-header">
            <h2 className="testimonials-title">{companyConfig.testimonials.title}</h2>
            <p className="testimonials-subtitle">{companyConfig.testimonials.subtitle}</p>
          </div>
          <div className="testimonials-grid">
            {companyConfig.testimonials.reviews.map((review) => (
              <div key={review.id} className="testimonial-card">
                <div className="testimonial-rating">
                  {[...Array(review.rating)].map((_, index) => (
                    <i key={index} className="fas fa-star"></i>
                  ))}
                </div>
                <p className="testimonial-text">"{review.review}"</p>
                <div className="testimonial-author">
                  <div className="testimonial-author-info">
                    <div className="testimonial-author-name">{review.name}</div>
                    <div className="testimonial-author-position">{review.position}</div>
                    <div className="testimonial-author-company">{review.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-us">
        <div className="why-choose-us-container">
          <div className="why-choose-us-header">
            <h2 className="why-choose-us-title">{companyConfig.whyChooseUs.title}</h2>
            <p className="why-choose-us-subtitle">{companyConfig.whyChooseUs.subtitle}</p>
          </div>
          <div className="why-choose-us-grid">
            {companyConfig.whyChooseUs.reasons.map((reason) => (
              <div key={reason.id} className="why-choose-card">
                <div className="why-choose-icon">
                  <i className={reason.icon}></i>
                </div>
                <h3 className="why-choose-card-title">{reason.title}</h3>
                <p className="why-choose-card-description">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="cta-content">
          <p className="cta-label">Transform Your Enterprise Today</p>
          <h2 className="cta-heading">Ready to future-proof your enterprise IT?</h2>
          <p className="cta-subtext">
            Join hundreds of global enterprises who trust {companyConfig.company.name} to accelerate their digital transformation journey.
          </p>
          <Link to="/contact">
            <button className="cta-button">Contact Us</button>
          </Link>
        </div>
      </section>
    </>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-main">
          {/* Column 1 - Brand & Contact */}
          <div className="footer-column footer-brand">
            <div className="footer-logo">
              <img src="/images/hero/main-logo.png" alt={`${companyConfig.company.name} Logo`} className="footer-logo-image" />
              <span>{companyConfig.company.name}</span>
            </div>
            <p className="footer-tagline">
              {companyConfig.company.description}
            </p>
            <div className="footer-contact-items">
              <a href={`mailto:${companyConfig.contact.email}`} className="footer-contact-item">
                <i className="fas fa-envelope"></i>
                <span>{companyConfig.contact.email}</span>
              </a>
              <a href={`tel:${companyConfig.contact.phone.replace(/\D/g, '')}`} className="footer-contact-item">
                <i className="fas fa-phone"></i>
                <span>{companyConfig.contact.phone}</span>
              </a>
              <div className="footer-contact-item">
                <i className="fas fa-map-marker-alt"></i>
                <span>{companyConfig.contact.address}</span>
              </div>
            </div>
          </div>

          {/* Column 2 - Quick Links */}
          <div className="footer-column">
            <h3 className="footer-heading">Quick Links</h3>
            <ul className="footer-links">
              <li><Link to="/about">About Us</Link></li>
              <li><Link to="/services">Services</Link></li>
              <li><Link to="/career">Careers</Link></li>
              <li><Link to="/contact">Contact</Link></li>
            </ul>
          </div>

          {/* Column 3 - Services */}
          <div className="footer-column">
            <h3 className="footer-heading">Services</h3>
            <ul className="footer-links">
              {companyConfig.services.map(service => (
                <li key={service.id}>
                  <Link to={`/services/${service.slug}`}>{service.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 - Connect with us */}
          <div className="footer-column footer-newsletter">
            <h3 className="footer-heading">Connect with us</h3>
            <div className="footer-social">
              <a 
                href={companyConfig.social.linkedin}
                target="_blank" 
                rel="noopener noreferrer"
                aria-label={`Follow ${companyConfig.company.name} on LinkedIn`}
                className="footer-social-icon"
              >
                <i className="fab fa-linkedin-in"></i>
              </a>
              <a 
                href={companyConfig.social.facebook}
                target="_blank" 
                rel="noopener noreferrer"
                aria-label={`Follow ${companyConfig.company.name} on Facebook`}
                className="footer-social-icon"
              >
                <i className="fab fa-facebook-f"></i>
              </a>
              <a 
                href={companyConfig.social.instagram}
                target="_blank" 
                rel="noopener noreferrer"
                aria-label={`Follow ${companyConfig.company.name} on Instagram`}
                className="footer-social-icon"
              >
                <i className="fab fa-instagram"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Footer Bottom Bar */}
        <div className="footer-bottom">
          <div className="footer-bottom-left">
            <p>&copy; 2026 {companyConfig.company.name}. All rights reserved.</p>
          </div>
          <div className="footer-bottom-right">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/cookies">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <div className="app">
          <Navbar />
          
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<About />} />
            <Route path="/our-industry" element={<OurIndustry />} />
            <Route path="/services" element={<ServicesDetailed />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/career" element={<Career />} />
            <Route path="/career/info" element={<CareerInfo />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
          </Routes>
          
          <Footer />

          {/* WhatsApp Chat Button */}
          <a
            href={`https://wa.me/${companyConfig.contact.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(companyConfig.contact.whatsappMessage)}`}
            className="whatsapp-float"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Chat on WhatsApp"
          >
            <i className="fab fa-whatsapp"></i>
          </a>

          {/* Cookie Consent Banner */}
          <CookieConsent />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;